import type { ComponentProps } from 'react';
import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import debounce from 'lodash-es/debounce';
import { backgroundPosition, backgroundSize, display, width } from 'styled-system';
import type {
  BackgroundPositionProps,
  BackgroundSizeProps,
  DisplayProps,
  WidthProps,
} from 'styled-system';

import {
  aspectRatio as aspectRatioUtil,
  objectFit,
  type AspectRatioProps,
  type ObjectFitProps,
} from 'common/theme/system-utilities';
import { breakpoints } from 'common/theme/theme';
import type { PrismicVideo } from 'common/types/video';
import {
  BIG_VARIANT_ASPECT_RATIO,
  HALF_SIZE_VARIANT_IMAGE_WIDTH,
  SLIM_VARIANT_ASPECT_RATIO,
} from '../../../constants';
import { ImageContainerType } from '../../../enums';
import type { TeaserProps } from '../../../types';
import { useVideoPlaybackTracking } from './hooks/playback-tracking';

interface ResponsiveVideoProps {
  type: ImageContainerType;
  video: PrismicVideo;
  isFullWidth: boolean;
  ctaButtonUrl?: TeaserProps['ctaButtonUrl'];
  onClick?: () => void;
  onError?: () => void;
  onVideoStart?: () => void;
  onVideoComplete?: () => void;
}

const Container = styled.div<WidthProps & DisplayProps & AspectRatioProps>`
  ${width};
  ${display};
  ${aspectRatioUtil};
`;

const containerStyles: Partial<ComponentProps<typeof Container>> = {
  display: 'block',
};

const StyledVideo = styled.video<
  WidthProps &
    DisplayProps &
    AspectRatioProps &
    ObjectFitProps &
    BackgroundSizeProps &
    BackgroundPositionProps & { mobilePoster: string; desktopPoster: string }
>`
  ${width};
  ${display};
  ${aspectRatioUtil};
  ${objectFit};
  ${backgroundSize};
  ${backgroundPosition};

  background-image: url(${props => props.mobilePoster});

  @media (min-width: ${breakpoints[1]}) {
    background-image: url(${props => props.desktopPoster});
  }
`;

const videoStyles: Partial<ComponentProps<typeof StyledVideo>> = {
  objectFit: 'cover',
  width: '100%',
  display: 'block',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
};

const DESKTOP_MEDIA_QUERY = `(min-width: ${breakpoints[1]})`;
const RESIZE_DEBOUNCE_TIME = 100;

const ResponsiveVideo = ({
  type,
  video,
  ctaButtonUrl,
  onClick,
  onError,
  onVideoStart,
  onVideoComplete,
  isFullWidth,
}: ResponsiveVideoProps) => {
  const aspectRatio =
    type === ImageContainerType.Big ? BIG_VARIANT_ASPECT_RATIO : SLIM_VARIANT_ASPECT_RATIO;

  const videoRef = useRef<HTMLVideoElement>(null);

  useVideoPlaybackTracking({ videoRef, onVideoStart, onVideoComplete });

  // Handle responsive video switching on window resize
  useEffect(() => {
    const breakpointValue = parseInt(breakpoints[1], 10);
    let isDesktop = window.innerWidth >= breakpointValue;

    const handleResize = debounce(() => {
      const IsViewDesktop = window.innerWidth >= breakpointValue;

      // Only reload if we've crossed the breakpoint
      if (IsViewDesktop !== isDesktop) {
        isDesktop = IsViewDesktop;

        if (videoRef.current) {
          const currentTime = videoRef.current.currentTime;
          videoRef.current.load();
          videoRef.current.currentTime = currentTime;
        }
      }
    }, RESIZE_DEBOUNCE_TIME);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  const handleClick = () => {
    if (ctaButtonUrl?.url && onClick) onClick();
  };

  const handleVideoError = () => {
    if (onError) onError();
  };

  return (
    <Container
      {...containerStyles}
      width={isFullWidth ? '100%' : ['100%', null, HALF_SIZE_VARIANT_IMAGE_WIDTH]}
      aspectRatio={aspectRatio}
      {...(ctaButtonUrl?.url
        ? {
            as: 'a',
            href: ctaButtonUrl.url,
            onClick: handleClick,
          }
        : {})}
    >
      <StyledVideo
        ref={videoRef}
        {...videoStyles}
        aspectRatio={aspectRatio}
        mobilePoster={video.mobile.poster}
        desktopPoster={video.desktop.poster}
        controls={false}
        autoPlay
        muted
        loop
        playsInline
        onError={handleVideoError}
      >
        {video.desktop.sources.map((source, index) => (
          <source
            key={`desktop-${index}`}
            src={source.src}
            type={source.type}
            media={DESKTOP_MEDIA_QUERY}
          />
        ))}
        {video.mobile.sources.map((source, index) => (
          <source key={`mobile-${index}`} src={source.src} type={source.type} />
        ))}
      </StyledVideo>
    </Container>
  );
};

export default ResponsiveVideo;
